import { css } from '@emotion/react';
import { differenceInCalendarDays } from 'date-fns/esm/fp';
import { observer } from 'mobx-react-lite';
import { Fragment, ReactElement, useEffect, useRef, useState } from 'react';
import { ChatDialogEvent, ChatDialogEventType, isWebkit } from 'sdk';

import { useSDK } from '../../../app';
import { styled } from '../../../styled';
import { isMobile } from '../../../utils/isMobile';
import { AgentTypingAlert } from './agentTypingAlert';
import { ChatMessagesItem } from './chatMessagesItem';
import { DateDelimiter } from './dateDelimiter';
import { ScrollbarContainer } from './scrollbarContainer';

type Interface = { className: string };

const style = css`
	display: flex;
	flex-direction: column-reverse;
	margin: 0;
	list-style-type: none;
	padding: 0 16px;

	display: flex;
	flex-direction: column-reverse;

	& > div:first-of-type {
		padding-bottom: 2px;
	}

	& > * {
		clear: both;
		& i {
			opacity: ${isMobile ? '1' : '0'};
			position: absolute;
			cursor: pointer;
		}
		&:hover i {
			opacity: 1;
			transition: opacity 0.15s;
		}
	}

	/*${isWebkit ? 'padding-right: 5px' : ''}*/
`;

const isPrevDay = (msg: ChatDialogEvent, prevMsg: ChatDialogEvent | undefined): boolean => {
	if (!prevMsg) {
		return true;
	}
	const difference = differenceInCalendarDays(new Date(prevMsg.creationTimestamp), new Date(msg.creationTimestamp));
	if (difference) {
		return true;
	}
	return false;
};

const MESSAGE_TYPES_WITHOUT_AVATAR = [ChatDialogEventType.Selection];
function needsHideAvatar(eventsToRender: ChatDialogEvent[], messageIndex: number): boolean {
	let nextMessageIndex = messageIndex - 1;
	while (
		nextMessageIndex >= 0 &&
		!eventsToRender[nextMessageIndex].view.text &&
		MESSAGE_TYPES_WITHOUT_AVATAR.includes(eventsToRender[nextMessageIndex].view.type)
	) {
		nextMessageIndex--;
	}
	return (
		nextMessageIndex >= 0 && eventsToRender[nextMessageIndex].direction === eventsToRender[messageIndex].direction
	);
}

const Component = observer(({ className }: Interface): ReactElement => {
	const rootStore = useSDK();
	const { eventsStore, conversationsStore } = rootStore;
	const [dialogEventsForRender, setDialogEventsForRender] = useState(eventsStore.dialogEventsForRender);
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setDialogEventsForRender(eventsStore.dialogEventsForRender);
	}, [eventsStore.dialogEventsForRender, eventsStore.dialogEventsForRender.length]);

	useEffect(() => {
		const callbacks = conversationsStore.subscribeOnOperatorTyping(5 * 1000);

		return (): void => {
			callbacks.forEach((c) => c());
		};
	}, [conversationsStore]);

	return (
		<ScrollbarContainer listRef={listRef}>
			<div className={className} ref={listRef}>
				{conversationsStore.isAgentTyping && (
					<AgentTypingAlert message={conversationsStore.agentTypingMessage ?? ''} />
				)}
				{dialogEventsForRender?.map((msg, idx) => (
					<Fragment key={msg.id}>
						<ChatMessagesItem message={msg} hideAvatar={needsHideAvatar(dialogEventsForRender, idx)} />
						{isPrevDay(msg, dialogEventsForRender[idx + 1]) && (
							<DateDelimiter date={new Date(msg.creationTimestamp)} />
						)}
					</Fragment>
				))}
			</div>
		</ScrollbarContainer>
	);
});

const ChatMessagesList = styled(Component)(style);

export { ChatMessagesList };
